import { useCallback } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import { unlinkDmPatient } from '../../../../services/dmPatient';
import { DmShellLayout } from './DmShellLayout';
import { SuccessBanner, ErrorBanner, BlockedNotLinkedBanner } from './DmBanners';
import { SkeletonCard } from './DmSkeletons';
import { DmBtnSecondary, DmBtnDestructive } from './DmFormPrimitives';
import { LinkButton } from '../../overview/shared';
import type { ActionRunnerProps } from '../types';

function UnlinkDmPatientLanding({ patientId, onBack }: ActionRunnerProps) {
    // Read the DM link details from the patient card's local mirror — this is
    // what `syncDmPatientToOdo` writes after Create / Link-existing, and the
    // same source the "Linked · DM #..." chip in ActionsTab uses.
    const { data: card, isLoading: cardLoading, refetch: refetchCard } = usePatientCard(patientId);
    const dmId = card?.monitoring?.dmId ?? null;
    const dmProfileId = card?.monitoring?.dmProfileId ?? null;
    const dmLink = card?.monitoring?.dmLink ?? null;
    const appActivation = card?.monitoring?.dmAppActivationStatus ?? null;
    const monitoringStatus = card?.monitoring?.monitoringStatus ?? null;
    const isLinked = dmId != null;

    const mutation = useMutation({
        mutationFn: () => unlinkDmPatient(patientId),
        onSuccess: async () => {
            await refetchCard();
        }
    });

    const handleUnlink = useCallback(() => {
        mutation.reset();
        mutation.mutate();
    }, [mutation]);

    const errorMessage =
        mutation.error instanceof Error
            ? mutation.error.message
            : mutation.isError
              ? 'An unexpected error occurred.'
              : null;

    return (
        <DmShellLayout title="Unlink DM" onBack={onBack}>
            <div className="w-full max-w-2xl mx-auto flex flex-col gap-4">
                {cardLoading && <SkeletonCard />}

                {/*  Defensive: not linked (gating should prevent reaching here)  */}
                {!cardLoading && !isLinked && !mutation.isSuccess && (
                    <BlockedNotLinkedBanner onBack={onBack} />
                )}

                {/*  Success state  */}
                {!cardLoading && mutation.isSuccess && (
                    <>
                        <SuccessBanner message="Patient unlinked from Dental Monitoring." />
                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                                What just happened
                            </p>
                            <p className="text-sm text-gray-600 leading-relaxed">
                                The patient is no longer connected to their Dental Monitoring
                                account. The DM patient itself wasn't deleted — if you need to
                                re-link, use the "Link to existing DM patient" action.
                            </p>
                        </div>
                        <div className="flex justify-end pt-2">
                            <DmBtnSecondary onClick={onBack}>Back to actions</DmBtnSecondary>
                        </div>
                    </>
                )}

                {/*  Confirm flow  */}
                {!cardLoading && isLinked && !mutation.isSuccess && (
                    <>
                        {errorMessage && (
                            <ErrorBanner message={errorMessage} onRetry={handleUnlink} />
                        )}

                        <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-yellow-900">
                                    Unbind this patient from Dental Monitoring?
                                </p>
                                <p className="text-xs text-yellow-700 mt-1 leading-relaxed">
                                    This will unlink the patient from Dental Monitoring. The DM
                                    patient itself is <strong>not</strong> deleted - you can re-link
                                    them later if needed.
                                </p>
                            </div>
                        </div>

                        <div className="bg-white rounded-xl border border-gray-200 p-6">
                            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
                                Current DM link
                            </p>
                            <div className="grid grid-cols-[140px_1fr] gap-y-2 text-sm">
                                <span className="text-gray-500">DM ID</span>
                                <span className="font-mono text-gray-800">{dmId}</span>

                                <span className="text-gray-500">DM Profile ID</span>
                                <span className="font-mono text-gray-800">
                                    {dmProfileId ?? '—'}
                                </span>

                                {dmLink && (
                                    <>
                                        <span className="text-gray-500 self-center">
                                            Profile link
                                        </span>
                                        <div>
                                            <LinkButton
                                                label="Open in Dental Monitoring"
                                                href={dmLink}
                                            />
                                        </div>
                                    </>
                                )}

                                {appActivation && (
                                    <>
                                        <span className="text-gray-500">App activation</span>
                                        <span className="text-gray-800">{appActivation}</span>
                                    </>
                                )}

                                {monitoringStatus && (
                                    <>
                                        <span className="text-gray-500">Monitoring</span>
                                        <span className="text-gray-800">{monitoringStatus}</span>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <DmBtnSecondary onClick={onBack} disabled={mutation.isPending}>
                                Cancel
                            </DmBtnSecondary>
                            <DmBtnDestructive onClick={handleUnlink} disabled={mutation.isPending}>
                                {mutation.isPending ? 'Unlinking…' : 'Unlink DM'}
                            </DmBtnDestructive>
                        </div>
                    </>
                )}
            </div>
        </DmShellLayout>
    );
}

export { UnlinkDmPatientLanding };
